/**
 * フォントページ (ブランド別フォントスタック + タイポグラフィルール)
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business ブランド×書体のフォントスタックテーブルとタイポグラフィルールの表示・編集。
 * @graph-connects api [calls] typography.listStacks / upsertStack / listRules
 */
import { useToast } from '@cortex/web-design-system/components/ui/Toast';
import type { FontStack } from '@cortex/domain-creative/web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { orpc } from '../api/orpc.js';
import { useAuth } from '../components/auth-context.js';
import { useBrandScope } from '../components/brand-scope-context.js';
import { brandNameOf, matchesBrandScope } from '../lib/brand-scope.js';
import {
  BrandAttributionField,
  BrandBadge,
  EmptyState,
  Field,
  GhostButton,
  INPUT_CLASS,
  Modal,
  PrimaryButton,
  SectionHeading,
  StatusPill,
} from '../components/ui.js';

/** スタック編集ドラフト (brandId '' = 会社共通)。 */
interface StackDraft {
  id: string;
  brand: string;
  style: FontStack['style'];
  stackText: string;
  note: string;
  brandId: string;
  order: number;
}

/**
 * フォントページ。
 * @returns ページ要素
 * @graph-connects api [calls] typography.listStacks / upsertStack / listRules
 */
export function FontsPage(): JSX.Element {
  const auth = useAuth();
  const brandScope = useBrandScope();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const stacksQuery = useQuery(orpc.typography.listStacks.queryOptions({ input: {} }));
  const rulesQuery = useQuery(orpc.typography.listRules.queryOptions({ input: {} }));
  const stacks = (stacksQuery.data?.items ?? []).filter((stack) =>
    matchesBrandScope(stack.brandId, brandScope.scope),
  );
  const rules = (rulesQuery.data?.items ?? []).filter((rule) =>
    matchesBrandScope(rule.brandId, brandScope.scope),
  );

  const [draft, setDraft] = useState<StackDraft | null>(null);

  const upsertMutation = useMutation(
    orpc.typography.upsertStack.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.typography.key() });
        setDraft(null);
        showToast('success', 'フォントスタックを保存しました');
      },
      onError: (error) => {
        showToast('error', error.message);
      },
    }),
  );

  const styleLabels: Record<FontStack['style'], string> = {
    gothic: 'ゴシック (sans-serif)',
    mincho: '明朝 (serif)',
  };

  return (
    <div>
      <SectionHeading title="Font Stacks" />
      {rules.map((rule) => (
        <div key={rule.id} className="rule-card">
          <span
            className="rule-bar"
            style={{ background: rule.level === 'ng' ? 'var(--dh-ng)' : 'var(--dh-caution)' }}
          />
          <div className="flex items-start gap-2">
            <StatusPill level={rule.level} />
            <div>
              <span>{rule.text}</span>
              {(rule.font !== undefined ||
                rule.letterSpacingPercent !== undefined ||
                rule.minImageFontPx !== undefined) && (
                <div className="mt-1 text-xs text-muted">
                  {rule.font !== undefined && <span className="mr-3">フォント: {rule.font}</span>}
                  {rule.letterSpacingPercent !== undefined && (
                    <span className="mr-3">字間 {rule.letterSpacingPercent}%</span>
                  )}
                  {rule.minImageFontPx !== undefined && (
                    <span>画像内最小 {rule.minImageFontPx}px</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {stacks.length === 0 ? (
        <EmptyState message="フォントスタックが未登録です (seed 未実行)" />
      ) : (
        (['gothic', 'mincho'] as const).map((style) => {
          const styleStacks = stacks.filter((stack) => stack.style === style);
          if (styleStacks.length === 0) {
            return null;
          }
          return (
            <div key={style} className="mb-7">
              <h3 className="mb-3 mt-6 text-sm font-bold text-foreground">{styleLabels[style]}</h3>
              <div className="dh-table-frame">
                <table className="w-full text-body">
                  <thead>
                    <tr>
                      <th className="dh-th">ブランド</th>
                      <th className="dh-th">フォントスタック</th>
                      {auth.canEdit && <th className="dh-th" />}
                    </tr>
                  </thead>
                  <tbody>
                    {styleStacks.map((stack) => (
                      <tr key={stack.id} className="hover:bg-glass">
                        <td className="dh-td font-semibold text-foreground">
                          {stack.brand}
                          {stack.brandId !== undefined && (
                            <BrandBadge label={brandNameOf(brandScope.brands, stack.brandId)} />
                          )}
                        </td>
                        <td className="dh-td">
                          <code className="rounded border border-line-light bg-surface px-1.5 py-0.5 font-mono text-caption text-accent-text">
                            {stack.stack.join(', ')}
                          </code>
                          {stack.note !== undefined && (
                            <span className="ml-2 text-xs text-muted">({stack.note})</span>
                          )}
                        </td>
                        {auth.canEdit && (
                          <td className="dh-td text-right">
                            <button
                              type="button"
                              className="text-xs text-muted hover:text-foreground"
                              onClick={() => {
                                setDraft({
                                  id: stack.id,
                                  brand: stack.brand,
                                  style: stack.style,
                                  stackText: stack.stack.join(', '),
                                  note: stack.note ?? '',
                                  brandId: stack.brandId ?? '',
                                  order: stack.order,
                                });
                              }}
                            >
                              編集
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}

      <Modal
        open={draft !== null}
        title="フォントスタックを編集"
        onClose={() => {
          setDraft(null);
        }}
      >
        {draft && (
          <>
            <Field label="ブランド">
              <input
                className={INPUT_CLASS}
                value={draft.brand}
                onChange={(event) => {
                  setDraft({ ...draft, brand: event.target.value });
                }}
              />
            </Field>
            <Field label="フォントスタック (カンマ区切り・優先順)">
              <input
                className={INPUT_CLASS}
                value={draft.stackText}
                onChange={(event) => {
                  setDraft({ ...draft, stackText: event.target.value });
                }}
              />
            </Field>
            <Field label="補足 (任意)">
              <input
                className={INPUT_CLASS}
                value={draft.note}
                onChange={(event) => {
                  setDraft({ ...draft, note: event.target.value });
                }}
              />
            </Field>
            <BrandAttributionField
              brands={brandScope.brands}
              value={draft.brandId}
              onChange={(brandId) => {
                setDraft({ ...draft, brandId });
              }}
            />
            <div className="mt-4 flex justify-end gap-2">
              <GhostButton
                onClick={() => {
                  setDraft(null);
                }}
              >
                キャンセル
              </GhostButton>
              <PrimaryButton
                disabled={draft.brand === '' || upsertMutation.isPending}
                onClick={() => {
                  upsertMutation.mutate({
                    id: draft.id,
                    input: {
                      brand: draft.brand,
                      style: draft.style,
                      stack: draft.stackText
                        .split(',')
                        .map((font) => font.trim())
                        .filter((font) => font !== ''),
                      note: draft.note === '' ? undefined : draft.note,
                      brandId: draft.brandId === '' ? undefined : draft.brandId,
                      order: draft.order,
                    },
                  });
                }}
              >
                保存
              </PrimaryButton>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
